// FontAwesome to Lucide icon mapping for tour-loader
// This file maps FontAwesome icons used in tour-loader to their Lucide equivalents

import {
  Plus,
  Minus,
  Check,
  X,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Menu,
  Settings,
  Search,
  Edit,
  Trash,
  Eye,
  EyeOff,
  Home,
  User,
  Bell,
  Calendar,
  File,
  Folder,
  Image,
  Video,
  Play,
  Pause,
  Volume2,
  VolumeX,
  Star,
  Share2,
  Download,
  Upload,
  Copy,
  Save,
  Heart,
  Film,
  Ruler,
  Minimize,
  Expand,
  Plane,
  ShoppingBag,
  ShoppingCart,
  Route,
  Crown,
  Phone,
  MapPin,
  MapPinned,
  Ellipsis,
  Mail,
  CalendarDays,
  FileText,
  Camera,
  Lightbulb,
  Info,
  AlertTriangle,
  CheckCircle,
  XCircle,
  AlertCircle,
  HelpCircle,
  Loader2,
  RotateCcw,
  RotateCw,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowDown,
  ExternalLink,
  Lock,
  Unlock,
  Globe,
  Code,
  Book,
  Bookmark,
  Tag,
  Tags,
  Clock,
  Timer,
  Activity,
  Gauge,
  Sun,
  Moon,
  ZoomIn,
  ZoomOut,
  Sliders,
  SlidersHorizontal,
  GripVertical,
  Move,
  Square,
  Circle,
  Triangle,
  Hexagon,
  type LucideIcon
} from 'lucide-react';

// FontAwesome to Lucide mapping object
export const fontAwesomeToLucideMap: Record<string, LucideIcon> = {
  // Basic actions
  faAdd: Plus,
  faPlus: Plus,
  faSolidPlus: Plus,
  faMinus: Minus,
  faCheck: Check,
  faX: X,
  faTimes: X,
  faClose: X,
  
  // Navigation
  faArrowLeft: ArrowLeft,
  faArrowRight: ArrowRight,
  faArrowUp: ArrowUp,
  faArrowDown: ArrowDown,
  faArrowUpRightFromSquare: ExternalLink,
  faChevronLeft: ChevronLeft,
  faChevronRight: ChevronRight,
  faChevronDown: ChevronDown,
  faChevronUp: ChevronUp,
  faAngleDown: ChevronDown,
  faAngleUp: ChevronUp,
  faCaretDown: ChevronDown,
  faCaretUp: ChevronUp,
  
  // Media controls
  faPlay: Play,
  faPause: Pause,
  faStop: Square,
  faVideo: Video,
  faFilm: Film,
  faVolumeUp: Volume2,
  faVolumeDown: Volume2,
  faVolumeOff: VolumeX,
  faVolumeMute: VolumeX,
  faVolumeSlash: VolumeX,
  
  // UI elements
  faBars: Menu,
  faMenu: Menu,
  faSearch: Search,
  faFilter: Search, // Using Search as Filter equivalent
  faSettings: Settings,
  faGear: Settings,
  faCog: Settings,
  faEllipsis: Ellipsis,
  faEllipsisV: Ellipsis,
  faMoreVert: Ellipsis,
  
  // Files and folders
  faFile: File,
  faFolder: Folder,
  faDownload: Download,
  faUpload: Upload,
  faSave: Save,
  faFilePdf: FileText,
  
  // Communication
  faEnvelope: Mail,
  faMail: Mail,
  faPhone: Phone,
  faMessage: Mail,
  faComment: Mail,
  faShare: Share2,
  faShareNodes: Share2,
  
  // Commerce
  faShoppingCart: ShoppingCart,
  faShoppingBag: ShoppingBag,
  faBasketShoppingSimple: ShoppingBag,
  faHeart: Heart,
  faStar: Star,
  
  // Location and maps
  faMap: MapPin,
  faMapPin: MapPin,
  faMapLocationDot: MapPinned,
  faLocation: MapPin,
  faRoute: Route,
  
  // Time and calendar
  faCalendar: Calendar,
  faCalendarAlt: CalendarDays,
  faClock: Clock,
  faTimer: Timer,
  
  // User and people
  faUser: User,
  faUsers: User, // Using single User icon
  faUserCheck: User,
  faCrown: Crown,
  
  // Tools and utilities
  faCamera: Camera,
  faRuler: Ruler,
  faLightbulb: Lightbulb,
  faExpand: Expand,
  faCompress: Minimize,
  faDrone: Plane,
  
  // Status and feedback
  faEye: Eye,
  faEyeOff: EyeOff,
  faCheckCircle: CheckCircle,
  faExclamationTriangle: AlertTriangle,
  faWarning: AlertTriangle,
  faInfo: Info,
  faInfoCircle: Info,
  faBell: Bell,
  faQuestionCircle: HelpCircle,
  faAlertCircle: AlertCircle,
  faXCircle: XCircle,
  
  // Edit and modify
  faEdit: Edit,
  faPen: Edit,
  faTrash: Trash,
  faTrash2: Trash,
  faCopy: Copy,
  faUndo: RotateCcw,
  faRedo: RotateCw,
  
  // Loading and progress
  faSpinner: Loader2,
  faCircleNotch: Loader2,
  faSync: RotateCcw,
  faRefresh: RotateCcw,

  // Media and accessibility
  faClosedCaptioning: FileText, // Using FileText as placeholder for closed captioning
  
  // Shapes and graphics
  faSquare: Square,
  faCircle: Circle,
  faTriangle: Triangle,
  faHexagon: Hexagon,
  
  // Business and office
  faBook: Book,
  faBookmark: Bookmark,
  faTag: Tag,
  faTags: Tags,
  
  // Technology
  faCode: Code,
  faGlobe: Globe,
  faBrowser: Globe,
  
  // Miscellaneous
  faHome: Home,
  faLock: Lock,
  faUnlock: Unlock,
  faActivity: Activity,
  faGauge: Gauge,
  faTachometer: Gauge,
  faSun: Sun,
  faMoon: Moon,
  faZoomIn: ZoomIn,
  faZoomOut: ZoomOut,
  faSliders: Sliders,
  faSlidersH: SlidersHorizontal,
  faGripVertical: GripVertical,
  faMove: Move,
  faArrowsUpDownLeftRight: Move,
  faResize: Move
};

// Helper function to get Lucide icon from FontAwesome icon name
export function getLucideIcon(fontAwesomeIconName: string): LucideIcon | undefined {
  return fontAwesomeToLucideMap[fontAwesomeIconName];
}

// Export commonly used Lucide icons for direct import
export {
  Plus,
  Minus,
  Check,
  X,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Menu,
  Settings,
  Search,
  Edit,
  Trash,
  Eye,
  EyeOff,
  Home,
  User,
  Bell,
  Calendar,
  File,
  Folder,
  Image,
  Video,
  Play,
  Pause,
  Volume2,
  Star,
  Share2,
  Download,
  Upload,
  Copy,
  Save,
  Loader2,
  type LucideIcon
};
